import { useState } from 'react';
import { Link } from 'react-router-dom';
import { newsArticles, categories } from '../data/newsArticles';

const slugify = (text) => {
  return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

const FeaturedContent = () => {
  const [activeFilter, setActiveFilter] = useState('all news');

  const filters = categories.map(category => ({ id: category.toLowerCase(), label: category }));

  const filteredArticles = activeFilter === 'all news'
    ? newsArticles
    : newsArticles.filter(article => article.category.toLowerCase() === activeFilter);

  return (
    <section className="section-padding">
      <div className="text-center mb-16">
        <h2 className="text-5xl md:text-6xl font-elegant font-bold gradient-text mb-6">
          Featured News & Events
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Stay updated with the latest news, events, and opportunities at Northwest Ballet Academy and Theater.
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap justify-center gap-4 mb-12">
        {filters.map((filter) => (
          <button
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
              activeFilter === filter.id
                ? 'bg-ballet-burgundy text-white shadow-elegant'
                : 'bg-gray-100 text-gray-600 hover:bg-ballet-pink hover:text-ballet-burgundy'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredArticles.map((article, index) => (
          <Link to={`/news/${slugify(article.title)}`} key={article.id} className="flex flex-col">
            <article
              className={`card-elevated group flex-grow ${article.featured ? 'lg:col-span-2' : ''}`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className={`w-full object-cover group-hover:scale-110 transition-transform duration-500 ${
                    article.featured ? 'h-64' : 'h-48'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>

              <div className="p-6">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <span className="font-semibold capitalize">{article.category}</span>
                  {article.featured && (
                    <>
                      <span className="mx-2">|</span>
                      <span className="font-semibold text-ballet-gold">Featured</span>
                    </>
                  )}
                  <span className="mx-2">|</span>
                  <span>{new Date(article.date).toLocaleDateString()}</span>
                </div>
                <h3 className={`font-elegant font-semibold text-ballet-burgundy mb-3 group-hover:text-ballet-gold transition-colors duration-300 ${
                  article.featured ? 'text-2xl' : 'text-xl'
                }`}>
                  {article.title}
                </h3>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {article.featured
                    ? article.excerpt.length > 100
                      ? `${article.excerpt.substring(0, 100)}...`
                      : article.excerpt
                    : article.excerpt.length > 80
                      ? `${article.excerpt.substring(0, 80)}...`
                      : article.excerpt}
                </p>
              </div>
            </article>
          </Link>
        ))}
      </div>

      {/* Enhanced Call to Action Section */}
      <div className="relative mt-20">
        <div className="bg-elegant-gradient rounded-4xl p-12 text-center text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-20 h-20 border border-white rounded-full"></div>
            <div className="absolute top-20 right-20 w-16 h-16 border border-white rounded-full"></div>
            <div className="absolute bottom-10 left-20 w-12 h-12 border border-white rounded-full"></div>
            <div className="absolute bottom-20 right-10 w-24 h-24 border border-white rounded-full"></div>
          </div>

          <div className="relative z-10">
            <h3 className="text-4xl md:text-5xl font-elegant font-bold mb-6">
              Ready to Begin Your Dance Journey?
            </h3>
            <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed opacity-90">
              Whether you're a beginner or an experienced dancer, Northwest Ballet Academy offers programs
              tailored to help you achieve your dance goals in a supportive and inspiring environment.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button className="btn-secondary shadow-glow">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Enroll in Classes
              </button>
              <button className="bg-white text-ballet-burgundy px-8 py-4 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 transform active:scale-95">
                <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Schedule a Tour
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/20">
              <div>
                <div className="text-3xl font-bold mb-2">18+</div>
                <div className="text-sm opacity-80">Years of Excellence</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">500+</div>
                <div className="text-sm opacity-80">Students Trained</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">50+</div>
                <div className="text-sm opacity-80">Annual Performances</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedContent;
